// Copyright (c) Brock Allen & Dominick Baier. All rights reserved.
// Licensed under the Apache License, Version 2.0. See LICENSE in the project root for license information.

import { Logger, Timer } from "./utils";
import type { SolaceCredentials } from "./User";
import type { UserManagerSettingsStore } from "./UserManagerSettings";
/**
 * @public
 */
export type SolaceCredentialsCallback = (...ev: unknown[]) => (Promise<void> | void);

/**
 * @public
 */
export class SolaceCredentialEvents {
    protected readonly _logger = new Logger("SolaceCredentialEvents");

    private readonly _expiringTimer = new Timer("Solace Credentials expiring");
    private readonly _expiredTimer = new Timer("Solace Credentials expired");
    private readonly _expiringNotificationTimeInSeconds: number;

    public constructor(settings: UserManagerSettingsStore) {
        this._expiringNotificationTimeInSeconds = settings.accessTokenExpiringNotificationTimeInSeconds;
    }

    public load(container: SolaceCredentials): void {
        const logger = this._logger.create("load");
        // only register events if there's credentials and it has an expiration
        if (container.username && container.expires_in !== undefined) {
            const duration = container.expires_in;
            logger.debug("solace credentials present, remaining duration:", duration);

            if (duration > 0) {
                // only register expiring if we still have time
                let expiring = duration - this._expiringNotificationTimeInSeconds;
                if (expiring <= 0) {
                    expiring = 1;
                }

                logger.debug("registering expiring timer, raising in", expiring, "seconds");
                this._expiringTimer.init(expiring);
            }
            else {
                logger.debug("canceling existing expiring timer because we're past expiration.");
                this._expiringTimer.cancel();
            }

            // if it's negative, it will still fire
            const expired = duration + 1;
            logger.debug("registering expired timer, raising in", expired, "seconds");
            this._expiredTimer.init(expired);
        }
        else {
            this._expiringTimer.cancel();
            this._expiredTimer.cancel();
        }
    }

    public unload(): void {
        this._logger.debug("unload: canceling existing solace credential timers");
        this._expiringTimer.cancel();
        this._expiredTimer.cancel();
    }

    /**
     * Add callback: Raised prior to the credentials expiring.
     */
    public addSolaceCredentialsExpiring(cb: SolaceCredentialsCallback): () => void {
        return this._expiringTimer.addHandler(cb);
    }
    /**
     * Remove callback: Raised prior to the credentials expiring.
     */
    public removeSolaceCredentialsExpiring(cb: SolaceCredentialsCallback): void {
        this._expiringTimer.removeHandler(cb);
    }

    /**
     * Add callback: Raised after the credentials have expired.
     */
    public addSolaceCredentialsExpired(cb: SolaceCredentialsCallback): () => void {
        return this._expiredTimer.addHandler(cb);
    }
    /**
     * Remove callback: Raised after the credentials have expired.
     */
    public removeSolaceCredentialsExpired(cb: SolaceCredentialsCallback): void {
        this._expiredTimer.removeHandler(cb);
    }
}